<template>
  <div class="dialog">
    <span class="cls" @click="closeList"></span>
    <div class="paper">
      <!-- Header -->
      <div class="header">
        <span class="back" @click="closeList">
          <i class="ph ph-arrow-left"></i>
        </span>
        <h2>커뮤니티</h2>
        <span class="action" @click="openEdit()">
          <i class="ph ph-plus-square"></i>
        </span>
      </div>

      <!-- Stories / Tabs -->
      <div class="tab-bar">
        <button 
          v-for="tab in tabs" 
          :key="tab.value"
          :class="['tab-item', { active: activeTab === tab.value }]"
          @click="changeTab(tab.value)"
        >
          {{ tab.label }}
        </button>
      </div>

      <!-- Feed -->
      <div class="feed">
        <!-- Post Card -->
        <div 
          class="post-card" 
          v-for="post in postList" 
          :key="post.idx"
        >
          <!-- Card Header -->
          <div class="card-header">
            <div class="user-info" @click="openView(post.idx)">
              <div class="avatar">
                <i class="ph-fill ph-user"></i>
              </div>
              <span class="username">{{ post.mb_id }}</span>
            </div>
            <button class="more-btn">
              <i class="ph ph-dots-three"></i>
            </button>
          </div>

          <!-- Card Image Slider -->
          <div 
            class="card-image" 
            v-if="post.parsedImages.length > 0"
          >
            <div class="image-slider">
              <div 
                class="image-track" 
                :style="{ transform: `translateX(-${getCurrentIndex(post.idx) * 100}%)` }"
              >
                <img 
                  v-for="(img, i) in post.parsedImages"
                  :key="i"
                  :src="img" 
                  alt="게시물 이미지" 
                  @click="openView(post.idx)"
                />
              </div>
            </div>
            
            <!-- Navigation Buttons (다중 이미지일 때만) -->
            <template v-if="post.parsedImages.length > 1">
              <button 
                class="nav-btn prev" 
                @click.stop="prevImage(post.idx)"
                v-show="getCurrentIndex(post.idx) > 0"
              >
                <i class="ph ph-caret-left"></i>
              </button>
              <button 
                class="nav-btn next" 
                @click.stop="nextImage(post.idx, post.parsedImages)"
                v-show="getCurrentIndex(post.idx) < post.parsedImages.length - 1"
              >
                <i class="ph ph-caret-right"></i>
              </button>
              
              <!-- Image Indicators -->
              <div class="image-indicators">
                <span 
                  v-for="(img, i) in post.parsedImages" 
                  :key="i"
                  :class="['dot', { active: getCurrentIndex(post.idx) === i }]"
                  @click.stop="goToImage(post.idx, i)"
                ></span>
              </div>
            </template>
            
            <!-- Image Counter Badge -->
            <span class="image-badge" v-if="post.parsedImages.length > 1">
              {{ getCurrentIndex(post.idx) + 1 }} / {{ post.parsedImages.length }}
            </span>
          </div>

          <!-- Card Image Placeholder -->
          <div 
            class="card-image placeholder" 
            @click="openView(post.idx)"
            v-else
          >
            <i class="ph ph-article"></i>
          </div>

          <!-- Card Actions -->
          <div class="card-actions">
            <div class="action-left">
              <button 
                class="action-btn" 
                :class="{ active: post.isLiked }"
                @click="toggleLike(post)"
              >
                <i :class="post.isLiked ? 'ph-fill ph-heart' : 'ph ph-heart'"></i>
              </button>
              <button class="action-btn" @click="openView(post.idx)">
                <i class="ph ph-chat-circle"></i>
              </button>
              <button class="action-btn">
                <i class="ph ph-paper-plane-tilt"></i>
              </button>
            </div>
            <button class="action-btn">
              <i class="ph ph-bookmark-simple"></i>
            </button>
          </div>

          <!-- Card Info -->
          <div class="card-info" @click="openView(post.idx)">
            <p class="likes" v-if="post.likeCount > 0">
              공감 <strong>{{ post.likeCount }}개</strong>
            </p>
            <p class="caption">
              <strong>{{ post.mb_id }}</strong>
              {{ post.title }}
            </p>
            <p class="preview" v-if="post.content">{{ truncateContent(post.content) }}</p>
            <button class="view-comments" v-if="post.commentCount > 0">
              댓글 {{ post.commentCount }}개 모두 보기
            </button>
            <span class="timestamp">{{ formatDate(post.created_at) }}</span>
          </div>
        </div>

        <!-- Empty State -->
        <div class="empty-state" v-if="postList.length === 0">
          <i class="ph ph-camera"></i>
          <h3>아직 게시물이 없습니다</h3>
          <p>첫 번째 사례를 공유해보세요</p>
          <button class="empty-btn" @click="openEdit()">작성하기</button>
        </div>

        <!-- Load More -->
        <div class="load-more" v-if="currentPage < totalPages">
          <button @click="loadMore">더 보기</button>
        </div>
      </div>

      <!-- Search FAB -->
      <button class="fab-search" @click="showSearchModal = true">
        <i class="ph ph-magnifying-glass"></i>
      </button>

      <!-- Search Modal -->
      <div class="search-modal" v-if="showSearchModal">
        <div class="search-backdrop" @click="showSearchModal = false"></div>
        <div class="search-content">
          <div class="search-input">
            <i class="ph ph-magnifying-glass"></i>
            <input 
              type="text" 
              v-model="searchKeyword" 
              placeholder="검색..."
              @keyup.enter="search"
              ref="searchInput"
            />
            <button class="search-clear" v-if="searchKeyword" @click="clearSearch">
              <i class="ph ph-x-circle"></i>
            </button>
          </div>
          <button class="search-cancel" @click="showSearchModal = false">취소</button>
        </div>
      </div>
    </div>

    <!-- Edit Dialog -->
    <SnsEdit 
      v-if="showEdit" 
      :oth_showSnsEdit="toggleEdit"
      :editIdx="editIdx"
    />

    <!-- View Dialog -->
    <SnsView 
      v-if="showView" 
      :oth_showSnsView="toggleView"
      :oth_showSnsEdit="toggleEdit"
      :postIdx="viewIdx"
    />
  </div>
</template>

<script>
import { ref, reactive, inject, onMounted } from "vue"
import SnsEdit from "@/components/dialogs/snsEdit.vue"
import SnsView from "@/components/dialogs/snsView.vue"

export default {
  components: { SnsEdit, SnsView },
  props: {
    oth_showSnsList: Function
  },
  setup(props) {
    const sql_query = inject('sql_query')
    const member_info = inject('member_info')
    const mb_id = member_info.mb_id

    const tabs = [
      { label: '전체', value: 'all' },
      { label: '인기', value: 'popular' },
      { label: '최신', value: 'recent' }
    ]
    const activeTab = ref('all')
    const searchKeyword = ref('')
    const postList = ref([])
    const showSearchModal = ref(false)
    const searchInput = ref(null)
    
    const currentPage = ref(1)
    const totalPages = ref(1)
    const perPage = 10

    const showEdit = ref(false)
    const editIdx = ref(null)
    const showView = ref(false)
    const viewIdx = ref(null)
    
    // 데이터 변경 여부 추적
    const hasDataChanged = ref(false)
    
    // 각 포스트별 현재 이미지 인덱스
    const imageIndexes = reactive({})
    
    // 클릭 쓰로틀 (애니메이션 중 클릭 방지)
    const isSliding = reactive({})

    const getPostList = async (append = false) => {
      let orderBy = 'p.created_at DESC'
      if (activeTab.value === 'popular') {
        orderBy = 'likeCount DESC, p.created_at DESC'
      }

      let whereClause = '1=1'
      if (searchKeyword.value.trim()) {
        whereClause = `(p.title LIKE '%${searchKeyword.value}%' OR p.content LIKE '%${searchKeyword.value}%')`
      }

      const countResult = await sql_query(`
        SELECT COUNT(*) as cnt FROM sns_post p WHERE ${whereClause}
      `)
      totalPages.value = Math.ceil(countResult[0].cnt / perPage) || 1

      const offset = (currentPage.value - 1) * perPage
      const rows = await sql_query(`
        SELECT 
          p.idx, p.mb_id, p.title, p.content, p.images, p.created_at,
          (SELECT COUNT(*) FROM likes WHERE target_table = 'sns_post' AND target_idx = p.idx) as likeCount,
          (SELECT COUNT(*) FROM comment WHERE target_table = 'sns_post' AND target_idx = p.idx AND status = 'normal') as commentCount,
          (SELECT COUNT(*) FROM likes WHERE target_table = 'sns_post' AND target_idx = p.idx AND mb_id = '${mb_id}') as isLiked
        FROM sns_post p
        WHERE ${whereClause}
        ORDER BY ${orderBy}
        LIMIT ${perPage} OFFSET ${offset}
      `)
      
      // 이미지 파싱 + 인덱스 초기화 + 좋아요 boolean 변환
      for (const post of rows) {
        post.parsedImages = parseImages(post.images)
        post.isLiked = post.isLiked > 0
        if (!(post.idx in imageIndexes)) {
          imageIndexes[post.idx] = 0
        }
      }
      
      if (append) {
        postList.value = [...postList.value, ...rows]
      } else {
        Object.keys(imageIndexes).forEach(key => delete imageIndexes[key])
        rows.forEach(post => imageIndexes[post.idx] = 0)
        postList.value = rows
      }
    }

    const changeTab = (tab) => {
      activeTab.value = tab
      currentPage.value = 1
      getPostList()
    }

    const search = () => {
      currentPage.value = 1
      getPostList()
      showSearchModal.value = false
    }

    const clearSearch = () => {
      searchKeyword.value = ''
      currentPage.value = 1
      getPostList()
    }

    const loadMore = () => {
      currentPage.value++
      getPostList(true)
    }

    // 이미지 배열 파싱 (데이터 로드 시 1회만 실행)
    const parseImages = (images) => {
      if (!images) return []
      try {
        const arr = JSON.parse(images)
        return Array.isArray(arr) ? arr : []
      } catch {
        return []
      }
    }
    
    // 현재 이미지 인덱스 가져오기
    const getCurrentIndex = (postIdx) => {
      return imageIndexes[postIdx] || 0
    }
    
    // 이전 이미지
    const prevImage = (postIdx) => {
      if (isSliding[postIdx]) return
      const current = imageIndexes[postIdx] || 0
      if (current > 0) {
        isSliding[postIdx] = true
        imageIndexes[postIdx] = current - 1
        setTimeout(() => isSliding[postIdx] = false, 300)
      }
    }
    
    // 다음 이미지
    const nextImage = (postIdx, parsedImages) => {
      if (isSliding[postIdx]) return
      const current = imageIndexes[postIdx] || 0
      if (current < parsedImages.length - 1) {
        isSliding[postIdx] = true
        imageIndexes[postIdx] = current + 1
        setTimeout(() => isSliding[postIdx] = false, 300)
      }
    }
    
    // 특정 이미지로 이동
    const goToImage = (postIdx, index) => {
      if (isSliding[postIdx]) return
      if (imageIndexes[postIdx] !== index) {
        isSliding[postIdx] = true
        imageIndexes[postIdx] = index
        setTimeout(() => isSliding[postIdx] = false, 300)
      }
    }
    
    // 좋아요 토글
    const toggleLike = async (post) => {
      if (post.isLiked) {
        await sql_query(`
          DELETE FROM likes 
          WHERE target_table = 'sns_post' AND target_idx = ${post.idx} AND mb_id = '${mb_id}'
        `)
        post.isLiked = false
        post.likeCount--
      } else {
        await sql_query(`
          INSERT INTO likes (mb_id, target_table, target_idx)
          VALUES ('${mb_id}', 'sns_post', ${post.idx})
        `)
        post.isLiked = true
        post.likeCount++
      }
      hasDataChanged.value = true
    }

    const truncateContent = (content) => {
      if (!content) return ''
      return content.length > 50 ? content.slice(0, 50) + '...' : content
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const now = new Date()
      const diff = now - date
      
      if (diff < 60000) return '방금 전'
      if (diff < 3600000) return `${Math.floor(diff / 60000)}분 전`
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}시간 전`
      if (diff < 604800000) return `${Math.floor(diff / 86400000)}일 전`
      if (diff < 2592000000) return `${Math.floor(diff / 604800000)}주 전`
      
      return `${date.getMonth() + 1}월 ${date.getDate()}일`
    }

    const toggleEdit = (show, options = {}) => {
      if (show) {
        editIdx.value = options.editIdx || null
        showEdit.value = true
      } else {
        showEdit.value = false
        editIdx.value = null
        if (options.refresh) {
          getPostList()
          hasDataChanged.value = true
        }
      }
    }

    const toggleView = (show, options = {}) => {
      if (show) {
        viewIdx.value = options.postIdx
        showView.value = true
      } else {
        showView.value = false
        viewIdx.value = null
        if (options.refresh) {
          getPostList()
          hasDataChanged.value = true
        }
      }
    }

    const openEdit = (idx = null) => {
      toggleEdit(true, { editIdx: idx })
    }

    const openView = (idx) => {
      toggleView(true, { postIdx: idx })
    }

    // 리스트 닫기 (데이터 변경 시 refresh 전달)
    const closeList = () => {
      props.oth_showSnsList(false, { refresh: hasDataChanged.value })
    }

    onMounted(() => {
      getPostList()
    })

    return {
      tabs,
      activeTab,
      searchKeyword,
      postList,
      currentPage,
      totalPages,
      showEdit,
      editIdx,
      showView,
      viewIdx,
      showSearchModal,
      searchInput,
      changeTab,
      search,
      clearSearch,
      loadMore,
      getCurrentIndex,
      prevImage,
      nextImage,
      goToImage,
      toggleLike,
      truncateContent,
      formatDate,
      toggleEdit,
      toggleView,
      openEdit,
      openView,
      closeList
    }
  }
}
</script>

<style scoped>
.dialog {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: var(--color-surface);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(var(--effect-blur));
}

.dialog > .cls {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  z-index: -1;
}

.dialog > .paper {
  position: relative;
  width: 90%;
  max-width: 470px;
  height: 85%;
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-lg);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header - Instagram Style */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--color-background-light);
  flex-shrink: 0;
}

.header > .back,
.header > .action {
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.header > .back > i,
.header > .action > i {
  font-size: 24px;
  color: var(--color-text-primary);
}

.header > h2 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  margin: 0;
}

/* Tab Bar */
.tab-bar {
  display: flex;
  border-bottom: 1px solid var(--color-background-light);
  flex-shrink: 0;
}

.tab-item {
  flex: 1;
  padding: 14px 0;
  border: none;
  background: none;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-third);
  cursor: pointer;
  position: relative;
  transition: color 0.2s;
}

.tab-item.active {
  color: var(--color-text-primary);
}

.tab-item.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 100%;
  height: 1px;
  background: var(--color-text-primary);
}

/* Feed */
.feed {
  flex: 1;
  overflow-y: auto;
}

/* Post Card */
.post-card {
  border-bottom: 1px solid var(--color-background-light);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #833AB4, #FD1D1D, #F77737);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
}

.avatar > i {
  width: 100%;
  height: 100%;
  background: var(--color-background);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: var(--color-text-secondary);
}

.username {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.more-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.more-btn > i {
  font-size: 20px;
  color: var(--color-text-primary);
}

/* Card Image */
.card-image {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  background: var(--color-background-light);
}

.image-slider {
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.image-track {
  display: flex;
  transition: transform 0.3s ease;
}

.image-track > img {
  flex-shrink: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
}

.card-image.placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 16/9;
  cursor: pointer;
}

.card-image.placeholder > i {
  font-size: 48px;
  color: var(--color-text-third);
}

/* Navigation Buttons */
.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  z-index: 2;
}

.nav-btn:hover {
  background: #fff;
  transform: translateY(-50%) scale(1.05);
}

.nav-btn:active {
  transform: translateY(-50%) scale(0.95);
}

.nav-btn > i {
  font-size: 16px;
  color: #333;
}

.nav-btn.prev {
  left: 8px;
}

.nav-btn.next {
  right: 8px;
}

/* Image Indicators (Dots) */
.image-indicators {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
  z-index: 2;
}

.image-indicators > .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.2s;
}

.image-indicators > .dot.active {
  background: #fff;
  transform: scale(1.2);
}

.image-indicators > .dot:hover {
  background: rgba(255, 255, 255, 0.8);
}

/* Image Badge */
.image-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 6px 10px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 20px;
  font-size: var(--font-size-xs);
  color: #fff;
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 2;
}

/* Card Actions */
.card-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
}

.action-left {
  display: flex;
  gap: 4px;
}

.action-btn {
  width: 40px;
  height: 40px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;
}

.action-btn:active {
  transform: scale(0.9);
}

.action-btn > i {
  font-size: 24px;
  color: var(--color-text-primary);
}

.action-btn.active > i {
  color: #ed4956;
}

/* Card Info */
.card-info {
  padding: 0 16px 16px;
  cursor: pointer;
}

.likes {
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  margin: 0 0 6px 0;
}

.caption {
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  margin: 0 0 4px 0;
  line-height: 1.4;
}

.preview {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin: 0 0 8px 0;
  line-height: 1.4;
}

.view-comments {
  display: block;
  border: none;
  background: none;
  padding: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-third);
  cursor: pointer;
  margin-bottom: 6px;
}

.timestamp {
  font-size: 10px;
  color: var(--color-text-third);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.empty-state > i {
  font-size: 64px;
  color: var(--color-text-third);
  margin-bottom: 16px;
}

.empty-state > h3 {
  font-size: var(--font-size-lg);
  color: var(--color-text-primary);
  margin: 0 0 8px 0;
}

.empty-state > p {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin: 0 0 20px 0;
}

.empty-btn {
  padding: 10px 20px;
  border: none;
  border-radius: var(--radius-sm);
  background: var(--color-brand);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-white);
  cursor: pointer;
}

/* Load More */
.load-more {
  padding: 20px;
  text-align: center;
}

.load-more > button {
  padding: 10px 24px;
  border: 1px solid var(--color-background-light);
  border-radius: var(--radius-md);
  background: none;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  cursor: pointer;
}

/* FAB Search */
.fab-search {
  position: absolute;
  bottom: 20px;
  right: 20px;
  width: 50px;
  height: 50px;
  border: none;
  border-radius: 50%;
  background: var(--color-brand);
  box-shadow: var(--shadow-lg);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fab-search > i {
  font-size: 22px;
  color: var(--color-text-white);
}

/* Search Modal */
.search-modal {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
}

.search-backdrop {
  position: absolute;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
}

.search-content {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  background: var(--color-background);
  border-bottom: 1px solid var(--color-background-light);
}

.search-input {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  background: var(--color-background-light);
  border-radius: var(--radius-md);
}

.search-input > i {
  font-size: 16px;
  color: var(--color-text-third);
}

.search-input > input {
  flex: 1;
  border: none;
  background: none;
  padding: 10px 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  outline: none;
}

.search-input > input::placeholder {
  color: var(--color-text-third);
}

.search-clear {
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  display: flex;
}

.search-clear > i {
  font-size: 18px;
  color: var(--color-text-third);
}

.search-cancel {
  border: none;
  background: none;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  cursor: pointer;
  white-space: nowrap;
}
</style>